import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';

export interface BillingRecord {
  id: number;
  date: string;
  /**
   * Where the credits came from. The backend still names this field `plan`
   * while the credit-system migration is in flight; it now carries a credit
   * type ("free" / "purchased") rather than a plan name.
   */
  plan: string;
  amount: number;
  currency: string;
  status: string;
  reference: string;
}

export interface BillingHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BillingRecord[];
}

export type CreditTypeFilter = 'free' | 'purchased';
export type SortFilter = 'latest' | 'oldest' | 'amount_desc' | 'amount_asc';

export interface BillingHistoryParams {
  creditType?: CreditTypeFilter;
  sort?: SortFilter;
  page?: number;
  search?: string;
}

/**
 * Hook to fetch billing history with optional filters
 */
export const useFetchBillingHistory = (params?: BillingHistoryParams) => {
  return useQuery({
    queryKey: ['billingHistory', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.creditType) searchParams.append('plan', params.creditType);
      if (params?.sort) searchParams.append('sort', params.sort);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.search) searchParams.append('search', params.search);

      const queryString = searchParams.toString();
      const url = `/billing/dashboard/billing-history/${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get<BillingHistoryResponse>(url);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
